import React, { useState, useEffect, useCallback } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { getNomEmpresaVisible } from "../../utils/Utilitats";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { API_URL } from "../../utils/config";
import HorariEditor from "./HorariEditor";
import "../dashboard_v2.css";
import "./horariempresa.css";

function AjudaHorariEmpresa({ onTancar }) {
  return (
    <div
      className="dv2-modalOverlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="horari-empresa-ajuda-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onTancar();
      }}
    >
      <div className="dv2-modal dv2-modal--wide">
        <div className="dv2-modal-head">
          <h3 id="horari-empresa-ajuda-title" className="dv2-modal-title">
            Com funciona l'Horari d'empresa
          </h3>
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Tancar"
            aria-label="Tancar"
            onClick={onTancar}
          >
            ✕
          </button>
        </div>
        <p className="dv2-modal-lead">
          L'horari predeterminat de l'empresa: quins dies i a quines hores es treballa. És una plantilla; cada empleat en fa una còpia pròpia.
        </p>

        <div className="dv2-help">
          <section className="dv2-help-section">
            <h4>Per a què serveix</h4>
            <p>
              Defineix l'horari predeterminat de l'empresa activa: quins dies i a quines hores es treballa. És una
              plantilla. Quan un empleat l'agafa, se'n fa una còpia pròpia; canviar-lo aquí no modifica els horaris ja
              assignats als empleats.
            </p>
            <p>
              Els festius i dies no laborables no es marquen aquí: van a <strong>Calendari laboral</strong>. Dissabtes i
              diumenges queden no laborables si no hi poses franges (es veuen com a «Tancat»).
            </p>
          </section>

          <section className="dv2-help-section">
            <h4>Si encara no n'hi ha</h4>
            <p>
              Prem <strong>Crear horari</strong>. El sistema el crea i l'assigna automàticament a l'empresa. Després ja
              pots afegir períodes.
            </p>
          </section>

          <section className="dv2-help-section">
            <h4>Períodes</h4>
            <p>
              Un horari es divideix en períodes quan la jornada no és la mateixa tot l'any (per exemple hivern i estiu).
              Cada període té un nom, unes dates i unes franges.
            </p>
            <ul>
              <li>
                <strong>+ Afegir període</strong>: primer tries les dates al calendari de l'any (un clic = inici, un
                altre = final; ‹ › canvia d'any) i després, si vols, el nom i les franges.
              </li>
              <li>Fes clic al títol del període per canviar-ne el nom.</li>
              <li>La paperera esborra tot el període (dates i franges).</li>
            </ul>
          </section>

          <section className="dv2-help-section">
            <h4>Dates del període</h4>
            <p>
              Un període pot tenir més d'un interval si el mateix patró de jornada es repeteix en dates no seguides (per
              exemple de gener a juny i de setembre a desembre).
            </p>
            <ul>
              <li>
                <strong>+ Afegir rang</strong> afegeix un altre interval. <strong>✎</strong> el canvia i <strong>✕</strong>{" "}
                l'esborra.
              </li>
              <li>
                Una data només pot pertànyer a un període. Si n'afegeixes un que es solapa, el sistema retalla
                automàticament l'altre.
              </li>
            </ul>
          </section>

          <section className="dv2-help-section">
            <h4>Franges horàries</h4>
            <p>
              Prem <strong>Editar franges</strong>. Marca els dies (Dl, Dt…), indica hora d'inici i final i prem{" "}
              <strong>+ Afegir als dies marcats</strong>.
            </p>
            <ul>
              <li>Pots posar més d'una franja el mateix dia (matí i tarda).</li>
              <li>Un dia sense cap franja queda «Tancat».</li>
              <li>
                <strong>Desar franges</strong> confirma els canvis. Esc o <strong>Cancel·lar</strong> els descarta.
              </li>
            </ul>
          </section>

          <section className="dv2-help-section">
            <h4>Tecles</h4>
            <p>
              <strong>Esc</strong> cancel·la el que estiguis editant (o tanca aquesta ajuda). <strong>Enter</strong>{" "}
              confirma o desa si el que hi ha és vàlid.
            </p>
          </section>
        </div>

        <div className="dv2-modal-actions">
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={onTancar}>
            Tancar
          </button>
        </div>
      </div>
    </div>
  );
}

function HorariEmpresa() {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const [horari, setHorari] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const idEmpresa = empresaSeleccionada?.IdEmpresa;
  const nomEmpresaVisible = getNomEmpresaVisible(empresaSeleccionada);

  const aplicarHorari = (nouHorari) => {
    setHorari(nouHorari);
  };

  const carregarHorari = useCallback(async () => {
    if (!idEmpresa) return;
    setHorari(null);
    setLoading(true);
    setError("");
    try {
      const res = await fetchAutenticat(`${API_URL}/HorariEmpresa.php?idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) {
        setHorari(data.horari);
      } else {
        setError(data.message || "No s'ha pogut carregar l'Horari d'empresa.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpresa]);

  useEffect(() => {
    carregarHorari();
  }, [carregarHorari]);

  useEffect(() => {
    if (!mostraAjuda) return undefined;
    const tancar = (event) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      setMostraAjuda(false);
    };
    window.addEventListener("keydown", tancar, true);
    return () => window.removeEventListener("keydown", tancar, true);
  }, [mostraAjuda]);

  const handleCrearHorari = async () => {
    try {
      const res = await fetchAutenticat(`${API_URL}/NouHorariEmpresa.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa }),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        alert(data.message || "No s'ha pogut crear l'Horari.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  const potGestionar = tincPermis(rolUsuari, "ACCES", "GESTIO_LABORAL");

  if (!potGestionar) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">No tens permisos per accedir a aquesta pantalla.</p>
      </div>
    );
  }

  if (!idEmpresa) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">Selecciona primer una Empresa per gestionar el seu Horari.</p>
      </div>
    );
  }

  const subtitol = [nomEmpresaVisible, horari?.NomHorari].filter(Boolean).join(" · ");
  const mostraCrear = !loading && !error && !horari;

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaHorariEmpresa onTancar={() => setMostraAjuda(false)} />}

      <div className="dv2-entity">
        <div className="dv2-pageHeader">
          <div className="dv2-pageHeader-main">
            <div className="dv2-pageHeader-icon" aria-hidden="true">
              🕒
            </div>
            <div>
              <h1 className="dv2-pageHeader-title">Horari d'empresa</h1>
              {subtitol && <p className="dv2-pageHeader-subtitle">{subtitol}</p>}
            </div>
          </div>
          <div className="dv2-pageHeader-actions">
            <button
              type="button"
              className="dv2-btn dv2-btn--icon dv2-btn--ghost"
              title="Com funciona aquesta pantalla"
              aria-label="Ajuda"
              onClick={() => setMostraAjuda(true)}
            >
              ?
            </button>
            {mostraCrear && (
              <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleCrearHorari}>
                Crear horari
              </button>
            )}
          </div>
        </div>

        <div className="dv2-entity-body">
          {loading && <p className="dv2-hint">Carregant...</p>}
          {error && <p className="horari-empresa-error">{error}</p>}

          {mostraCrear && (
            <p className="dv2-empty">Aquesta Empresa encara no té cap Horari predeterminat definit.</p>
          )}

          {!loading && horari && horari.Descripcio && (
            <div className="dv2-card">
              <div className="dv2-card-head">
                <span className="dv2-card-title">Descripció</span>
              </div>
              <div className="dv2-value">{horari.Descripcio}</div>
            </div>
          )}

          {!loading && horari && (
            <HorariEditor
              idEmpresa={idEmpresa}
              horari={horari}
              onHorariActualitzat={aplicarHorari}
              onRecarregar={carregarHorari}
              readOnly={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default HorariEmpresa;
